import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { supabase } from "src/lib/supabase";
import CustomerKpiStrip       from "./customers/CustomerKpiStrip";
import CustomersTable         from "./customers/CustomersTable";
import CustomerDrawer         from "./customers/CustomerDrawer";
import CustomerBulkActionBar  from "./customers/CustomerBulkActionBar";

// ── Pure filter function ────────────────────────────────────────────────────

function applyFilters(data, query, kpi, filters, sortField, sortDir) {
  let result = data.filter((c) => {
    const q = query.toLowerCase();
    if (q) {
      const searchable = [c.phone, c.name, c.id, c.email || ""].join(" ").toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    if (filters.status !== "all" && c.status !== filters.status) return false;
    if (filters.location !== "all" && !c.location.toLowerCase().includes(filters.location.toLowerCase())) return false;
    if (filters.activityLevel !== "all") {
      if (filters.activityLevel === "high"   && c.totalOrders < 20) return false;
      if (filters.activityLevel === "medium" && (c.totalOrders < 5 || c.totalOrders >= 20)) return false;
      if (filters.activityLevel === "low"    && c.totalOrders >= 5) return false;
    }
    if (filters.ordersRange !== "all") {
      if (filters.ordersRange === "0"    && c.totalOrders !== 0) return false;
      if (filters.ordersRange === "1-5"  && (c.totalOrders < 1  || c.totalOrders > 5))  return false;
      if (filters.ordersRange === "5-20" && (c.totalOrders < 5  || c.totalOrders > 20)) return false;
      if (filters.ordersRange === "20+"  && c.totalOrders < 20)  return false;
    }
    return true;
  });

  if (kpi === "active")     result = result.filter((c) => c.status === "active");
  if (kpi === "flagged")    result = result.filter((c) => c.status === "flagged");
  if (kpi === "suspended")  result = result.filter((c) => c.status === "suspended");
  if (kpi === "high_value") result = result.filter((c) => c.totalSpend >= 30000);

  if (sortField) {
    result = [...result].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === "string") return sortDir * av.localeCompare(bv);
      return sortDir * (av - bv);
    });
  }

  return result;
}

const DEFAULT_FILTERS = {
  status:        "all",
  location:      "all",
  activityLevel: "all",
  ordersRange:   "all",
};

// ── Component ───────────────────────────────────────────────────────────────

export default function AdminCustomers() {
  // Safe context destructuring (even though this component uses its own toast)
  const context = useOutletContext() || {};
  const { showToast: contextShowToast } = context;
  
  const [customers, setCustomers]               = useState([]);
  const [selectedIds, setSelectedIds]           = useState(new Set());
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery]           = useState("");
  const [activeKpi, setActiveKpi]               = useState("all");
  const [sortField, setSortField]               = useState(null);
  const [sortDir, setSortDir]                   = useState(1);
  const [currentPage, setCurrentPage]           = useState(1);
  const [loading, setLoading]                   = useState(true);
  const [toast, setToast]                       = useState(null);
  const [filters, setFilters]                   = useState(DEFAULT_FILTERS);

  const PAGE_SIZE   = 10;
  const searchTimer = useRef(null);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, phone, email, location, account_status, created_at")
        .eq("role", "client")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Real order counts and spend per customer
      const { data: orderRows, error: orderErr } = await supabase
        .from("job_requests")
        .select("customer_id, estimated_price, payment_status");
      if (orderErr) console.error("Failed to load order aggregates:", orderErr); // non-fatal

      const statsByCustomer = {};
      (orderRows || []).forEach(r => {
        if (!statsByCustomer[r.customer_id]) statsByCustomer[r.customer_id] = { orders: 0, spend: 0 };
        statsByCustomer[r.customer_id].orders += 1;
        if (r.payment_status === "paid") statsByCustomer[r.customer_id].spend += (r.estimated_price || 0);
      });

      const shaped = (data || []).map(c => ({
        id: c.id,
        name: c.full_name || "Unnamed",
        phone: c.phone || "",
        email: c.email || "",
        location: c.location || "Unknown",
        totalOrders: statsByCustomer[c.id]?.orders || 0,
        totalSpend: statsByCustomer[c.id]?.spend || 0,
        status: c.account_status === "suspended" ? "suspended" : c.account_status === "flagged" ? "flagged" : "active",
        dateJoined: c.created_at,
        lastActive: "—",
      }));

      setCustomers(shaped);
    } catch (err) {
      console.error("Failed to load customers:", err);
      showToast(`Failed to load customers: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleSearchInput = useCallback((val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearchQuery(val); setCurrentPage(1); }, 250);
  }, []);

  const filteredCustomers = useMemo(
    () => applyFilters(customers, searchQuery, activeKpi, filters, sortField, sortDir),
    [customers, searchQuery, activeKpi, filters, sortField, sortDir]
  );

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCustomers.slice(start, start + PAGE_SIZE);
  }, [filteredCustomers, currentPage]);

  const totalPages = Math.ceil(filteredCustomers.length / PAGE_SIZE);

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortField(field); setSortDir(1); }
    setCurrentPage(1);
  }, [sortField]);

  const handleSelectAll = useCallback((checked) => {
    const pageIds = new Set(paginatedCustomers.map((c) => c.id));
    if (checked) {
      setSelectedIds((prev) => new Set([...prev, ...pageIds]));
    } else {
      setSelectedIds((prev) => { const next = new Set(prev); pageIds.forEach((id) => next.delete(id)); return next; });
    }
  }, [paginatedCustomers]);

  const handleSelect = useCallback((id, checked) => {
    setSelectedIds((prev) => { const next = new Set(prev); checked ? next.add(id) : next.delete(id); return next; });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS); setActiveKpi("all"); setSearchQuery(""); setCurrentPage(1);
    showToast("Filters cleared", "success");
  }, [showToast]);

  const handleFlagCustomer = useCallback((id) => {
    setCustomers((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      const newStatus = c.status === "flagged" ? "active" : "flagged";
      return { ...c, status: newStatus };
    }));
    const c = customers.find((x) => x.id === id);
    showToast(`${c?.name} ${c?.status === "flagged" ? "unflagged" : "flagged"}`, c?.status === "flagged" ? "success" : "error");
  }, [customers, showToast]);

  const handleSuspendCustomer = useCallback((id) => {
    setCustomers((prev) => prev.map((c) => {
      if (c.id !== id) return c;
      return { ...c, status: c.status === "suspended" ? "active" : "suspended" };
    }));
    const c = customers.find((x) => x.id === id);
    showToast(`${c?.name} status updated`, "success");
  }, [customers, showToast]);

  const handleMessageCustomer = useCallback((id) => {
    const c = customers.find((x) => x.id === id);
    showToast(`Messaging ${c?.name} — coming soon`, "info");
  }, [customers, showToast]);

  const handleDrawerUpdate = useCallback((updated) => {
    setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCustomer(updated);
  }, []);

  const handleBulkActivate = useCallback(() => {
    setCustomers((prev) => prev.map((c) => selectedIds.has(c.id) ? { ...c, status: "active" } : c));
    showToast(`${selectedIds.size} customer(s) activated`, "success");
    setSelectedIds(new Set());
  }, [selectedIds, showToast]);

  const handleBulkSuspend = useCallback(() => {
    setCustomers((prev) => prev.map((c) => selectedIds.has(c.id) ? { ...c, status: "suspended" } : c));
    showToast(`${selectedIds.size} customer(s) suspended`, "success");
    setSelectedIds(new Set());
  }, [selectedIds, showToast]);

  const handleBulkExport = useCallback(() => {
    const selected = customers.filter((c) => selectedIds.has(c.id));
    const csv = [
      ["ID", "Name", "Phone", "Email", "Location", "Orders", "Total Spend", "Status", "Joined", "Last Active"],
      ...selected.map((c) => [c.id, c.name, c.phone, c.email || "", c.location, c.totalOrders, c.totalSpend, c.status, c.dateJoined, c.lastActive]),
    ].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `customers_export_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${selectedIds.size} customer(s)`, "success");
  }, [customers, selectedIds, showToast]);

  const locations = useMemo(
    () => ["all", ...Array.from(new Set(customers.map((c) => c.location.split(",")[0].trim())))],
    [customers]
  );

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 24, gap: 10, flexWrap: "wrap" }}>
        {selectedIds.size > 0 ? (
          <CustomerBulkActionBar
            selectedCount={selectedIds.size}
            onActivate={handleBulkActivate}
            onSuspend={handleBulkSuspend}
            onExport={handleBulkExport}
            onClear={() => setSelectedIds(new Set())}
          />
        ) : (
          <>
            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 10, padding: "0 14px", border: "1px solid #E2E8F0", height: 40, flex: "1 1 240px", maxWidth: 320 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                <circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" />
              </svg>
              <input
                type="text"
                placeholder="Search by phone, name…"
                onChange={(e) => handleSearchInput(e.target.value)}
                style={{ border: "none", outline: "none", fontSize: 13.5, color: "#334155", width: "100%", background: "transparent", fontFamily: "'Plus Jakarta Sans', sans-serif", height: "100%" }}
              />
            </div>

            {/* Status */}
            <select value={filters.status} onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setCurrentPage(1); }}
              style={{ height: 40, border: "1px solid #E2E8F0", borderRadius: 10, padding: "0 12px", fontSize: 13, color: "#334155", background: "#fff", cursor: "pointer", outline: "none", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="flagged">Flagged</option>
              <option value="suspended">Suspended</option>
            </select>

            {/* Location */}
            <select value={filters.location} onChange={(e) => { setFilters((f) => ({ ...f, location: e.target.value })); setCurrentPage(1); }}
              style={{ height: 40, border: "1px solid #E2E8F0", borderRadius: 10, padding: "0 12px", fontSize: 13, color: "#334155", background: "#fff", cursor: "pointer", outline: "none", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {locations.map((loc) => <option key={loc} value={loc}>{loc === "all" ? "All Locations" : loc}</option>)}
            </select>

            {/* Activity Level */}
            <select value={filters.activityLevel} onChange={(e) => { setFilters((f) => ({ ...f, activityLevel: e.target.value })); setCurrentPage(1); }}
              style={{ height: 40, border: "1px solid #E2E8F0", borderRadius: 10, padding: "0 12px", fontSize: 13, color: "#334155", background: "#fff", cursor: "pointer", outline: "none", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <option value="all">All Activity</option>
              <option value="high">High (20+)</option>
              <option value="medium">Medium (5–20)</option>
              <option value="low">Low (0–5)</option>
            </select>

            {/* Orders Range */}
            <select value={filters.ordersRange} onChange={(e) => { setFilters((f) => ({ ...f, ordersRange: e.target.value })); setCurrentPage(1); }}
              style={{ height: 40, border: "1px solid #E2E8F0", borderRadius: 10, padding: "0 12px", fontSize: 13, color: "#334155", background: "#fff", cursor: "pointer", outline: "none", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <option value="all">All Orders</option>
              <option value="0">0 orders</option>
              <option value="1-5">1–5 orders</option>
              <option value="5-20">5–20 orders</option>
              <option value="20+">20+ orders</option>
            </select>

            {/* Export */}
            <button
              onClick={() => { setSelectedIds(new Set(filteredCustomers.map((c) => c.id))); showToast("All visible customers selected — use bulk export", "info"); }}
              style={{ height: 40, padding: "0 16px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#64748B", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}
            >
              Export CSV
            </button>
          </>
        )}
      </div>

      {/* ── KPI Strip ── */}
      <CustomerKpiStrip
        customers={customers}
        activeKpi={activeKpi}
        onKpiClick={(kpi) => { setActiveKpi(activeKpi === kpi ? "all" : kpi); setCurrentPage(1); }}
      />

      {/* ── Table ── */}
      <CustomersTable
        customers={paginatedCustomers}
        allCustomers={filteredCustomers}
        selectedIds={selectedIds}
        onRowClick={setSelectedCustomer}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        sortField={sortField}
        sortDir={sortDir}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        loading={loading}
        onClearFilters={handleClearFilters}
        onFlagCustomer={handleFlagCustomer}
        onSuspendCustomer={handleSuspendCustomer}
        onMessageCustomer={handleMessageCustomer}
      />

      {/* ── Drawer ── */}
      <CustomerDrawer
        customer={selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
        onUpdate={handleDrawerUpdate}
        showToast={showToast}
      />

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "success" ? "#0F172A" : toast.type === "info" ? "#3B82F6" : "#DC2626",
          color: "#fff", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          zIndex: 999, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}